import * as _ from 'lodash';
import * as React from 'react';
import { observer } from 'mobx-react';
import * as MicroErrors from '@journeyapps-platform/micro-errors';
import { DeveloperInviteStatus } from '@journeyapps-platform/types-accounts-hub';
import { DeveloperInviteModel } from '@journeyapps-platform/lib-journey-core';
import { useSystem } from '../../../../hooks/useSystem';
import { isAllowed } from '../../../../hooks/useAllowed';
import { System } from '../../../../System';
import {
  AbstractEditDeveloperDialogWidget,
  EditDeveloperSubmitParams,
  EditDeveloperValues,
  showErrorDialog
} from './AbstractEditDeveloperDialogWidget';
import { OrgStore } from '../../../../stores/OrgStore';
import { DialogStore } from '../../../../stores/DialogStore';
import { createDeveloper } from './EditParentDeveloperDialogWidget';
import { Routes, useJourneyNavigation } from '../../../../routes';
import { UserModelAction } from '@journeyapps-platform/cardinal-catalog';

export type EditDeveloperDialogWidgetProps = {
  onSubmitOrCancel: () => any;
};

export async function createDeveloperInvite(
  values: EditDeveloperValues,
  org_id: string,
  system: System,
  orgStore: OrgStore,
  dialogStore: DialogStore
) {
  // Create new invite
  try {
    const invite = await system.accountV5Client.createInvite({
      email: values.email,
      org_id: org_id,
      requested_role_ids: values.roleIds
    });

    if (!invite) {
      console.error(`Empty developer invite returned in response`, invite);
      await showErrorDialog(dialogStore);
      return;
    }

    system.developerInviteStore.upsert(invite);
    await orgStore.current_org?.loadDeveloperInvites();
  } catch (error) {
    switch (true) {
      case MicroErrors.matchesErrorCode(error, 'NOT_FOUND'):
        // User not found. Create them as a new user in the parent org
        await createDeveloper(values, org_id, system, orgStore, dialogStore);
        break;
      case MicroErrors.matchesErrorCode(error, 'RESOURCE_CONFLICT'):
        // ResourceConflict - Email already taken
        await dialogStore.errorDialog({
          subTitle: 'Invite already exists',
          errorMessage: 'This invite already exists. Delete the existing invite to reinvite the developer.'
        });
        break;
      default:
        console.error(`Failed to add developer`, error);
        await showErrorDialog(dialogStore, MicroErrors.getErrorData(error)?.details);
        break;
    }
  }
}

const onSubmit = async (params: EditDeveloperSubmitParams<DeveloperInviteModel>) => {
  const { context, developerReference, stores, values } = params;

  const { system, orgStore, dialogStore } = stores;

  const allowedToInvite = await isAllowed({
    system,
    resource: system.developerInviteStore.generate({
      id: '',
      responded_at: '',
      status: DeveloperInviteStatus.PENDING,
      metadata: {},
      org_id: context.org_id,
      requested_role_ids: [],
      applied_role_ids: [],
      user_id: ''
    }),
    action: UserModelAction.CREATE
  });

  if (!allowedToInvite) {
    await dialogStore.errorDialog({
      title: 'Not Allowed',
      errorMessage: `You do not have permission to invite an existing developer. Ask your administrator to add this developer, or to elevate your permissions.`
    });
    return;
  }

  if (!developerReference) {
    await createDeveloperInvite(values, context.org_id, system, orgStore, dialogStore);
  } else {
    // Update existing invite
    try {
      await system.accountV5Client.updateInvite({
        id: developerReference.id,
        requested_role_ids: values.roleIds
      });

      await orgStore.current_org?.loadDeveloperInvites();
    } catch (error) {
      console.error(`Failed to update developer invite`, error);
      await showErrorDialog(dialogStore);
    }
  }
};

export const EditDeveloperInviteDialogWidget: React.FC<EditDeveloperDialogWidgetProps> = observer((props) => {
  const system = useSystem();

  const handler = useJourneyNavigation(Routes.EDIT_INVITE);

  const { onSubmitOrCancel } = props;
  const { params } = handler;

  return (
    <AbstractEditDeveloperDialogWidget<DeveloperInviteModel>
      handler={handler}
      contextProvider={() => {
        return {
          org_id: params.org_id,
          model_id: params.invite_id
        };
      }}
      developerProvider={async (id) => {
        if (!id) {
          return null;
        }
        return system.developerInviteStore.resolve(id);
      }}
      initialValuesProvider={(developer) => {
        if (developer.model.status === DeveloperInviteStatus.ACCEPTED) {
          return {
            email: developer.email,
            roleIds: developer.model.applied_role_ids
          };
        }
        return {
          email: developer.email,
          roleIds: developer.model.requested_role_ids
        };
      }}
      formSubmitter={onSubmit}
      onSubmitOrCancel={onSubmitOrCancel}
    />
  );
});
